import axios, { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAlert } from "@/hooks/useAlert";
import { useIsAnonymousUser } from "@/hooks/useIsAnonymousUser";
import {
  PARTNER_AUTHORIZATION,
  PUBLIC_AUTHORIZATION,
  TOKEN_URL,
} from "@/utils/apiUrls";
import { accessTokenAtom, refreshTokenAtom } from "@/utils/atoms";

interface Props {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}

interface PartnerLoginCredentials {
  email: string;
  password: string;
}

export const usePartnerLogin = (options?: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);
  const [, setIsAnonymousPartner] = useIsAnonymousUser();
  const navigate = useNavigate();
  const loginAsPartner = async (credentials: PartnerLoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        grant_type: "pos_login",
        username: credentials.email,
        password: credentials.password,
      });

      const response = await axios({
        method: "POST",
        url: TOKEN_URL.toString(),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: PARTNER_AUTHORIZATION,
        },
        data: params,
      });

      if (!response.data.access_token) {
        throw new Error("No access token received");
      }

      setAccessToken(response.data.access_token);
      setRefreshToken(response.data.refresh_token || null);

      options?.onSuccess?.();
      //ToDo add redirect to Partner dashboard
      // navigate("/partner/dashboard");
      navigate("/portal/cockpit/");
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError);

      options?.onError?.(axiosError);
    } finally {
      setIsLoading(false);
    }
  };

  const loginAsPublicPartner = async () => {
    try {
      const response = await axios({
        method: "POST",
        url: `${TOKEN_URL.toString()}?grant_type=pos_login&username=reseller@ambient.digital&password=Test123!`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: PUBLIC_AUTHORIZATION,
        },
      });
      setAccessToken(response.data.access_token);
      if (response.data.refresh_token) {
        setRefreshToken(response.data.refresh_token);
      }
      setIsAnonymousPartner(true);
    } catch (error) {
      console.error(`Public Partner login failed:`, error);
    }
  };

  return {
    loginAsPartner,
    loginAsPublicPartner,
    isLoading,
    error,
  };
};
